/**
 * Multi-venue calendar logic — keep in sync with teniso-uzsiemimai-app/venueCalendarUtils.js
 */
export const LOCATIONS = {
  SEB_ARENA: 'seb_arena',
  VRM_COURTS: 'vrm_courts',
  BERNARDAI: 'bernardinai_courts'
};

export const AVAILABILITY_STATES = {
  UNAVAILABLE: 'unavailable',
  SEB_ARENA: 'seb_arena',
  VRM_COURTS: 'vrm_courts',
  BERNARDAI: 'bernardinai_courts'
};

export const TRAINING_VENUES = [
  { id: LOCATIONS.SEB_ARENA, label: 'Seb Arena', color: '#2196f3' },
  { id: LOCATIONS.VRM_COURTS, label: 'VRM', color: '#4caf50' },
  { id: LOCATIONS.BERNARDAI, label: 'Bernardinai', color: '#ed6c02' }
];

export const ALL_TRAINING_VENUE_IDS = TRAINING_VENUES.map((v) => v.id);

/** Default venues selected for coach calendar editing (Seb Arena only). */
export const DEFAULT_COACH_EDIT_VENUE_IDS = [LOCATIONS.SEB_ARENA];

export const VENUE_COLORS = Object.fromEntries(TRAINING_VENUES.map((v) => [v.id, v.color]));

export const OTHER_VENUE_BUFFER_MINUTES = 30;

export const UNAVAILABLE_COLOR = '#e0e0e0';

export const createEmptyVenuesMap = () => ({
  [LOCATIONS.SEB_ARENA]: false,
  [LOCATIONS.VRM_COURTS]: false,
  [LOCATIONS.BERNARDAI]: false
});

export const createDefaultSlotObject = (time, duration = 30) => ({
  time,
  duration,
  venues: createEmptyVenuesMap()
});

export const normalizeSlot = (slot) => {
  if (!slot) return createDefaultSlotObject('');
  const time = slot.time || '';
  const duration = slot.duration || 30;
  if (slot.venues && typeof slot.venues === 'object') {
    const venues = createEmptyVenuesMap();
    ALL_TRAINING_VENUE_IDS.forEach((id) => {
      venues[id] = slot.venues[id] === true;
    });
    return { time, duration, venues };
  }
  const venues = createEmptyVenuesMap();
  const a = String(slot.availability || AVAILABILITY_STATES.UNAVAILABLE).trim().toLowerCase();
  if (a === AVAILABILITY_STATES.SEB_ARENA) venues[LOCATIONS.SEB_ARENA] = true;
  else if (a === AVAILABILITY_STATES.VRM_COURTS) venues[LOCATIONS.VRM_COURTS] = true;
  else if (a === AVAILABILITY_STATES.BERNARDAI) venues[LOCATIONS.BERNARDAI] = true;
  return { time, duration, venues };
};

export const normalizeTimeSlots = (timeSlots) => (timeSlots || []).map(normalizeSlot);

export const stripLegacyAvailability = (slot) => {
  const normalized = normalizeSlot(slot);
  return { time: normalized.time, duration: normalized.duration, venues: normalized.venues };
};

export const getActiveVenuesForCoach = () => ALL_TRAINING_VENUE_IDS.slice();

export const getSelectedVenuesForEdit = (selection) => {
  const active = getActiveVenuesForCoach();
  if (!Array.isArray(selection) || selection.length === 0) return [];
  return selection.filter((id) => active.includes(id));
};

/** Bulk toggle: all selected on → all off; otherwise all selected on. */
export const coachToggleSlot = (slot, selectedVenueIds) => {
  const normalized = normalizeSlot(slot);
  const venues = { ...normalized.venues };
  const ids = getSelectedVenuesForEdit(selectedVenueIds);
  if (ids.length === 0) return normalized;
  const allAvailable = ids.every((id) => venues[id] === true);
  ids.forEach((id) => {
    venues[id] = !allAvailable;
  });
  return { time: normalized.time, duration: normalized.duration, venues };
};

export const getVenuesAvailableInSlot = (slot, venueFilter = null) => {
  const { venues } = normalizeSlot(slot);
  const filter =
    venueFilter && venueFilter.length > 0 ? venueFilter : ALL_TRAINING_VENUE_IDS;
  return filter.filter((id) => venues[id] === true);
};

export const isSlotBookable = (slot) =>
  getVenuesAvailableInSlot(slot).length > 0;

export const timeToMinutes = (time) => {
  const [h, m] = String(time || '0:0').split(':').map(Number);
  return h * 60 + (m || 0);
};

export const halfHourRangeContains = (blockStart, blockEnd, slotTime) => {
  const slotStart = timeToMinutes(slotTime);
  const slotEnd = slotStart + 30;
  return slotStart < blockEnd && slotEnd > blockStart;
};

export const getReservationEndMinutes = (reservation) => {
  if (reservation.endTime) return timeToMinutes(reservation.endTime);
  return timeToMinutes(reservation.startTime) + (reservation.duration || 60);
};

export const getReservationBlockMinutes = (reservation, venueId) => {
  if (!reservation || reservation.status === 'cancelled') return null;
  const startMin = timeToMinutes(reservation.startTime);
  const endMin = getReservationEndMinutes(reservation);
  const bookedVenue = reservation.location || LOCATIONS.SEB_ARENA;
  if (bookedVenue === venueId) {
    return { startMin, endMin };
  }
  return {
    startMin: startMin - OTHER_VENUE_BUFFER_MINUTES,
    endMin: endMin + OTHER_VENUE_BUFFER_MINUTES
  };
};

export const isHalfHourBlockedByReservations = (date, time, venueId, reservations) => {
  const list = Array.isArray(reservations) ? reservations : [];
  for (const res of list) {
    if (res.date !== date || res.status === 'cancelled') continue;
    const block = getReservationBlockMinutes(res, venueId);
    if (block && halfHourRangeContains(block.startMin, block.endMin, time)) return true;
  }
  return false;
};

export const rangesOverlapMinutes = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && aEnd > bStart;

export const hasReservationConflictForLocation = (
  date,
  startTime,
  duration,
  location,
  existingReservations
) => {
  const startMin = timeToMinutes(startTime);
  const endMin = startMin + duration;
  const list = Array.isArray(existingReservations) ? existingReservations : [];
  for (const res of list) {
    if (res.date !== date || res.status === 'cancelled') continue;
    const block = getReservationBlockMinutes(res, location);
    if (block && rangesOverlapMinutes(startMin, endMin, block.startMin, block.endMin)) {
      return { hasConflict: true, conflictingReservation: res };
    }
  }
  return { hasConflict: false };
};

export const VENUE_HOVER_SCALE = 1.14;

/** 3-venue diagonal: corner leg % (top-left); bottom-right uses (100 - this). Middle band stays visible. */
export const THREE_VENUE_CORNER_PCT = 62;
export const THREE_VENUE_MID_T = 0.6;

export const buildVenueDiagonalGradient = (venueIds) => {
  if (!venueIds || venueIds.length === 0) return UNAVAILABLE_COLOR;
  if (venueIds.length === 1) return VENUE_COLORS[venueIds[0]] || UNAVAILABLE_COLOR;
  const colors = venueIds.map((id) => VENUE_COLORS[id] || UNAVAILABLE_COLOR);
  if (venueIds.length === 2) {
    return `linear-gradient(135deg, ${colors[0]} 50%, ${colors[1]} 50%)`;
  }
  const midPct = THREE_VENUE_MID_T * 100;
  return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[0]} 33.33%, ${colors[1]} 33.33%, ${colors[1]} ${midPct}%, ${colors[2]} ${midPct}%, ${colors[2]} 100%)`;
};

/** Pick venue from click inside a 135deg diagonal-split cell. relX/relY are 0–1 from top-left. */
export const getVenueIdFromDiagonalClick = (venueIds, relX, relY) => {
  if (!venueIds || venueIds.length === 0) return null;
  if (venueIds.length === 1) return venueIds[0];
  const t = (relX + relY) / 2;
  if (venueIds.length === 2) {
    return venueIds[t < 0.5 ? 0 : 1];
  }
  const idx = t < 1 / 3 ? 0 : t < THREE_VENUE_MID_T ? 1 : 2;
  return venueIds[Math.min(idx, venueIds.length - 1)];
};

/** Clip paths for 135deg diagonal wedges — must match buildVenueDiagonalGradient / getVenueIdFromDiagonalClick. */
export const VENUE_WEDGE_CLIP_PATHS = {
  2: [
    'polygon(0 0, 100% 0, 0 100%)',
    'polygon(100% 0, 100% 100%, 0 100%)'
  ],
  3: (() => {
    const c = `${THREE_VENUE_CORNER_PCT}%`;
    const r = `${100 - THREE_VENUE_CORNER_PCT}%`;
    return [
      `polygon(0 0, ${c} 0, 0 ${c})`,
      `polygon(${c} 0, 100% 0, 100% ${r}, ${r} 100%, 0 100%, 0 ${c})`,
      `polygon(100% 100%, 100% ${r}, ${r} 100%)`
    ];
  })()
};

export const VENUE_WEDGE_TRANSFORM_ORIGINS = {
  2: ['28% 28%', '72% 72%'],
  3: ['22% 22%', '50% 50%', '78% 78%']
};

export const getSlotVenueBackground = (slot, visibleVenueIds) => {
  const available = getVenuesAvailableInSlot(slot, visibleVenueIds);
  if (available.length === 0) return UNAVAILABLE_COLOR;
  return buildVenueDiagonalGradient(available);
};

export const getLocationDisplayName = (locationId) => {
  const venue = TRAINING_VENUES.find((v) => v.id === locationId);
  return venue ? venue.label : 'Nenurodyta';
};

export const isSlotCoveredByAnyReservation = (date, time, reservations) => {
  const list = Array.isArray(reservations) ? reservations : [];
  const slotStart = timeToMinutes(time);
  const slotEnd = slotStart + 30;
  return list.some((res) => {
    if (res.date !== date || res.status === 'cancelled') return false;
    const startMin = timeToMinutes(res.startTime);
    const endMin = getReservationEndMinutes(res);
    return rangesOverlapMinutes(slotStart, slotEnd, startMin, endMin);
  });
};

export const getReservationCoveringSlot = (date, time, reservations) => {
  const list = Array.isArray(reservations) ? reservations : [];
  const slotStart = timeToMinutes(time);
  const slotEnd = slotStart + 30;
  return list.find((res) => {
    if (res.date !== date || res.status === 'cancelled') return false;
    const startMin = timeToMinutes(res.startTime);
    const endMin = getReservationEndMinutes(res);
    return rangesOverlapMinutes(slotStart, slotEnd, startMin, endMin);
  });
};

export const isVenueAvailableForDuration = (
  date,
  startTime,
  duration,
  venueId,
  availableSlots,
  reservations,
  isSlotAvailableForBookingFn
) => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = startMinutes + duration;
  for (let minute = startMinutes; minute < endMinutes; minute += 30) {
    const time = `${Math.floor(minute / 60)
      .toString()
      .padStart(2, '0')}:${(minute % 60).toString().padStart(2, '0')}`;
    if (isSlotAvailableForBookingFn && !isSlotAvailableForBookingFn(date, time)) {
      return false;
    }
    if (isHalfHourBlockedByReservations(date, time, venueId, reservations)) {
      return false;
    }
    const slot = availableSlots[date]?.timeSlots?.find((s) => s.time === time);
    if (!slot || !getVenuesAvailableInSlot(slot, [venueId]).includes(venueId)) {
      return false;
    }
  }
  return true;
};
